import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Calendar, 
  Building, 
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  X,
  MapPin,
  User,
  DollarSign,
  FileText
} from 'lucide-react';
import {
  getQuoteRequests,
  QuoteRequest,
  RateType,
  CardBrand,
  CardRate,
  CARD_BRANDS,
  assignQuoteRate,
} from '../services/quoteService';
import RateHistory from './RateHistory';
import { useToast } from './Toast';
import { logger } from '../utils/logger';

type RateDraft = Record<CardBrand, { percent: string; fee: string }>;

const emptyRateDraft = (): RateDraft => ({
  visa: { percent: '', fee: '' },
  mastercard: { percent: '', fee: '' },
  discover: { percent: '', fee: '' },
  amex: { percent: '', fee: '' },
});

const toRateDraft = (cards?: Record<CardBrand, CardRate>): RateDraft => {
  if (!cards) return emptyRateDraft();
  return CARD_BRANDS.reduce((draft, brand) => {
    const percent = cards[brand.id]?.percent;
    const fee = cards[brand.id]?.fee;
    draft[brand.id] = {
      percent: percent ? String(percent) : '',
      fee: fee ? String(fee) : '',
    };
    return draft;
  }, emptyRateDraft());
};

const toSavedRates = (draft: RateDraft): Record<CardBrand, CardRate> =>
  CARD_BRANDS.reduce((cards, brand) => {
    cards[brand.id] = {
      percent: Number(draft[brand.id].percent) || 0,
      fee: Number(draft[brand.id].fee) || 0,
    };
    return cards;
  }, {} as Record<CardBrand, CardRate>);

const QuoteManagement: React.FC<{ view?: 'active' | 'approved' }> = ({ view }) => {
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<QuoteRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateMode, setRateMode] = useState<'assign' | 'view'>('assign');
  const [rateType, setRateType] = useState<RateType>('fixed');
  const [rateCards, setRateCards] = useState<RateDraft>(emptyRateDraft());
  const [isSavingRate, setIsSavingRate] = useState(false);
  const { success, error: showError } = useToast();

  useEffect(() => {
    const loadQuoteRequests = async () => {
      try {
        setIsLoading(true);
        const requests = await getQuoteRequests();
        setQuoteRequests(requests);
      } catch (error) {
        logger.error('Error loading quote requests:', error);
        showError('Failed to load quote requests', 'Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadQuoteRequests();
  }, []);

  const filteredRequests = quoteRequests.filter(request => {
    const matchesSearch = request.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (request.productName && request.productName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeRequests = filteredRequests.filter(request => 
    request.status === 'pending' || request.status === 'in-progress'
  );
  
  const completedRequests = filteredRequests.filter(request => 
    request.status === 'completed'
  );
  
  const declinedRequests = filteredRequests.filter(request => 
    request.status === 'declined'
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in-progress': return <DollarSign className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'declined': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const openRateModal = (request: QuoteRequest, mode: 'assign' | 'view') => {
    setSelectedRequest(request);
    setRateMode(mode);
    setRateType(request.rateType || 'fixed');
    setRateCards(
      mode === 'assign' && request.rateStatus === 'revision_requested'
        ? emptyRateDraft()
        : toRateDraft(request.rateCards)
    );
    setShowRateModal(true);
  };

  const updateCardRate = (brand: CardBrand, field: 'percent' | 'fee', value: string) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setRateCards((prev) => ({
      ...prev,
      [brand]: {
        ...prev[brand],
        [field]: value,
      },
    }));
  };

  const handleSaveRate = async () => {
    if (!selectedRequest) return;
    const savedRates = toSavedRates(rateCards);
    setIsSavingRate(true);
    try {
      await assignQuoteRate(selectedRequest, rateType, savedRates);
      setQuoteRequests((prev) =>
        prev.map((req) =>
          req.id === selectedRequest.id
            ? {
                ...req,
                rateType,
                rateCards: savedRates,
                rateHistory: selectedRequest.rateCards
                  ? [
                      ...(req.rateHistory || []),
                      {
                        type: selectedRequest.rateType || 'fixed',
                        cards: selectedRequest.rateCards,
                        status: selectedRequest.rateStatus === 'offered' ? 'superseded' : selectedRequest.rateStatus,
                        offeredAt: selectedRequest.rateOfferedAt,
                        respondedAt: selectedRequest.rateRespondedAt,
                      },
                    ]
                  : req.rateHistory,
                rateStatus: 'offered',
                status: 'in-progress',
                rateOfferedAt: new Date(),
              }
            : req
        )
      );
      success('Store rate sent', 'This rate is for the store. The customer can accept it, and you can change it later.');
      setShowRateModal(false);
    } catch (error) {
      logger.error('Error assigning rate:', error);
      showError('Failed to assign rate', 'Please try again.');
    } finally {
      setIsSavingRate(false);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
        <span className="ml-3 text-gray-600">Loading quote requests...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {view === 'approved' ? 'Approved quotes' : view === 'active' ? 'Active quotes' : 'Quote Requests'}
          </h2>
          <p className="text-gray-600">
            {view === 'approved'
              ? 'Accepted store rates — you can still change them'
              : 'Assign a store rate. You can update it later if pricing changes.'}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Package className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{quoteRequests.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{activeRequests.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedRequests.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Building className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {quoteRequests.filter(r => {
                  const now = new Date();
                  const requestDate = r.createdAt;
                  return requestDate.getMonth() === now.getMonth() && requestDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by business name or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        </div>
        {!view && (
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">Rate sent</option>
            <option value="completed">Accepted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
        )}
      </div>

      {view !== 'approved' && activeRequests.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No active quotes right now.
        </div>
      )}

      {/* Active Requests */}
      {view !== 'approved' && activeRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
            Active Requests ({activeRequests.length})
          </h3>
          <div className="grid gap-4">
            {activeRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{request.businessName}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status === 'in-progress' ? 'rate sent' : request.status.replace('-', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        {request.productName || 'General Inquiry'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {request.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Business Type</p>
                    <p className="text-sm text-gray-600">{request.businessType}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Monthly Volume</p>
                    <p className="text-sm text-gray-600">{request.monthlyVolume}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Timeline</p>
                    <p className="text-sm text-gray-600">{request.timeline}</p>
                  </div>
                </div>
                
                {request.additionalNeeds && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700">Additional Requirements</p>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{request.additionalNeeds}</p>
                  </div>
                )}
                
                {request.rateStatus === 'revision_requested' && (
                  <p className="mb-3 text-sm text-orange-700">Customer requested another rate.</p>
                )}
                {request.rateCards && request.rateStatus !== 'revision_requested' && (
                  <p className="mb-3 text-sm text-blue-700">
                    Rate {request.rateStatus === 'offered' ? 'sent' : request.rateStatus}: {request.rateType === 'ic_plus' ? 'I/C plus' : 'Fixed'}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      openRateModal(
                        request,
                        request.rateStatus === 'revision_requested' || !request.rateCards ? 'assign' : 'view'
                      )
                    }
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors"
                  >
                    {request.rateStatus === 'revision_requested' || !request.rateCards ? 'Assign rate' : 'View rate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Declined/Denied Requests - Show on top */}
      {view !== 'active' && declinedRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            Declined Requests ({declinedRequests.length})
          </h3>
          <div className="grid gap-4">
            {declinedRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{request.businessName}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status === 'in-progress' ? 'rate sent' : request.status.replace('-', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        {request.productName || 'General Inquiry'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {request.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Business Type</p>
                    <p className="text-sm text-gray-600">{request.businessType}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Monthly Volume</p>
                    <p className="text-sm text-gray-600">{request.monthlyVolume}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Timeline</p>
                    <p className="text-sm text-gray-600">{request.timeline}</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => openRateModal(request, 'view')}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors"
                  >
                    View rate
                  </button>
                  <button
                    onClick={() => openRateModal(request, 'assign')}
                    className="px-4 py-2 bg-white border border-gray-900 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Change rate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Requests */}
      {view === 'approved' && completedRequests.length === 0 && declinedRequests.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No approved quotes yet.
        </div>
      )}

      {view !== 'active' && completedRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            Completed Requests ({completedRequests.length})
          </h3>
          <div className="grid gap-4">
            {completedRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{request.businessName}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status === 'in-progress' ? 'rate sent' : request.status.replace('-', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        {request.productName || 'General Inquiry'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {request.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Business Type</p>
                    <p className="text-sm text-gray-600">{request.businessType}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Monthly Volume</p>
                    <p className="text-sm text-gray-600">{request.monthlyVolume}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Timeline</p>
                    <p className="text-sm text-gray-600">{request.timeline}</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => openRateModal(request, 'view')}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors"
                  >
                    View rate
                  </button>
                  <button
                    onClick={() => openRateModal(request, 'assign')}
                    className="px-4 py-2 bg-white border border-gray-900 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Change rate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No quote requests</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'No requests match your current filters.' 
              : 'Quote requests will appear here when customers submit them.'
            }
          </p>
        </div>
      )}

      {showRateModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {rateMode === 'assign' ? 'Choose / assign store rate' : 'Store rate'}
                </h3>
                <p className="text-sm text-gray-500">{selectedRequest.businessName}</p>
              </div>
              <button type="button" onClick={() => setShowRateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">Rate type</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={rateMode === 'view'}
                    onClick={() => setRateType('fixed')}
                    className={`rounded-lg border px-4 py-3 text-left ${
                      rateType === 'fixed' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">Fixed</p>
                    <p className="text-xs text-gray-500">Blended rate per card type</p>
                  </button>
                  <button
                    type="button"
                    disabled={rateMode === 'view'}
                    onClick={() => setRateType('ic_plus')}
                    className={`rounded-lg border px-4 py-3 text-left ${
                      rateType === 'ic_plus' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">I/C plus</p>
                    <p className="text-xs text-gray-500">Interchange plus your markup</p>
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900 mb-3">Rate by card type</p>
                <div className="space-y-3">
                  {CARD_BRANDS.map((brand) => (
                    <div key={brand.id} className="grid grid-cols-1 sm:grid-cols-[7rem_1fr_1fr] gap-3 items-center rounded-lg border border-gray-200 p-3">
                      <p className="font-medium text-gray-900">{brand.label}</p>
                      <label className="block">
                        <span className="text-xs text-gray-500">%</span>
                        <div className="relative mt-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g. 2.65"
                            disabled={rateMode === 'view'}
                            value={rateCards[brand.id].percent}
                            onChange={(e) => updateCardRate(brand.id, 'percent', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm disabled:bg-gray-50"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                        </div>
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">$ per transaction</span>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g. 0.10"
                            disabled={rateMode === 'view'}
                            value={rateCards[brand.id].fee}
                            onChange={(e) => updateCardRate(brand.id, 'fee', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 pl-7 pr-3 py-2 text-sm disabled:bg-gray-50"
                          />
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Monthly volume</p>
                  <p className="text-gray-900">{selectedRequest.monthlyVolume || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Business type</p>
                  <p className="text-gray-900">{selectedRequest.businessType || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="text-gray-900">{selectedRequest.email || selectedRequest.customerEmail || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-900">{selectedRequest.phone || '—'}</p>
                </div>
              </div>

              {selectedRequest.rateStatus && selectedRequest.rateStatus !== 'none' && (
                <p className="text-sm text-gray-600">
                  Customer status:{' '}
                  <span className="font-medium text-gray-900">
                    {selectedRequest.rateStatus === 'revision_requested' ? 'requested another rate' : selectedRequest.rateStatus}
                  </span>
                </p>
              )}

              <RateHistory quote={selectedRequest} />
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDetailsModal(true)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
              >
                View details
              </button>
              <button
                type="button"
                onClick={() => setShowRateModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
              >
                Close
              </button>
              {rateMode === 'view' && (
                <button
                  type="button"
                  onClick={() => {
                    setRateMode('assign');
                    if (selectedRequest.rateStatus === 'revision_requested') {
                      setRateCards(emptyRateDraft());
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-900 text-gray-900"
                >
                  {selectedRequest.rateStatus === 'accepted' ? 'Change rate' : selectedRequest.rateStatus === 'revision_requested' ? 'Assign new rate' : 'Edit rate'}
                </button>
              )}
              {rateMode === 'assign' && (
                <button
                  type="button"
                  onClick={handleSaveRate}
                  disabled={isSavingRate}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-50"
                >
                  {isSavingRate ? 'Sending...' : selectedRequest.rateStatus === 'accepted' ? 'Send updated rate' : 'Send rate to customer'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">Quote Request Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Business Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2" />
                  Business Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Business Name</p>
                    <p className="text-sm text-gray-900">{selectedRequest.businessName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Business Type</p>
                    <p className="text-sm text-gray-900">{selectedRequest.businessType}</p>
                  </div>
                  {selectedRequest.businessAddress && (
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-700 flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        Address
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedRequest.businessAddress}
                        {selectedRequest.city && `, ${selectedRequest.city}`}
                        {selectedRequest.state && `, ${selectedRequest.state}`}
                        {selectedRequest.zipCode && ` ${selectedRequest.zipCode}`}
                      </p>
                    </div>
                  )}
                  {selectedRequest.yearsInBusiness && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Years in Business</p>
                      <p className="text-sm text-gray-900">{selectedRequest.yearsInBusiness}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(selectedRequest.firstName || selectedRequest.lastName) && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Name</p>
                      <p className="text-sm text-gray-900">
                        {selectedRequest.firstName} {selectedRequest.lastName}
                      </p>
                    </div>
                  )}
                  {selectedRequest.email && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        Email
                      </p>
                      <p className="text-sm text-gray-900">{selectedRequest.email}</p>
                    </div>
                  )}
                  {selectedRequest.phone && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 flex items-center">
                        <Phone className="h-4 w-4 mr-1" />
                        Phone
                      </p>
                      <p className="text-sm text-gray-900">{selectedRequest.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Financial Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedRequest.monthlyVolume && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Monthly Volume</p>
                      <p className="text-sm text-gray-900">{selectedRequest.monthlyVolume}</p>
                    </div>
                  )}
                  {selectedRequest.annualRevenue && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Annual Revenue</p>
                      <p className="text-sm text-gray-900">{selectedRequest.annualRevenue}</p>
                    </div>
                  )}
                  {selectedRequest.currentProcessor && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Processor</p>
                      <p className="text-sm text-gray-900">{selectedRequest.currentProcessor}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* System Requirements */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="h-5 w-5 mr-2" />
                  System Requirements
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedRequest.systemType && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">System Type</p>
                      <p className="text-sm text-gray-900">{selectedRequest.systemType}</p>
                    </div>
                  )}
                  {selectedRequest.timeline && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Timeline</p>
                      <p className="text-sm text-gray-900">{selectedRequest.timeline}</p>
                    </div>
                  )}
                  {selectedRequest.additionalNeeds && (
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-700">Additional Needs</p>
                      <p className="text-sm text-gray-900 bg-white p-3 rounded-lg">{selectedRequest.additionalNeeds}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Message */}
              {selectedRequest.message && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Additional Message
                  </h4>
                  <p className="text-sm text-gray-900 bg-white p-3 rounded-lg">{selectedRequest.message}</p>
                </div>
              )}

              {/* Request Metadata */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Request ID: {selectedRequest.id}</span>
                  <span>Submitted: {selectedRequest.createdAt.toLocaleDateString()} {selectedRequest.createdAt.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteManagement;
